import { useState, useMemo, useContext, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import { scaleOffsets } from '~/modules/resource-management/ResourcingOverview/util';
import { ResourceOverviewToolbarContext } from '~/modules/resource-management/ResourcingOverview/hooks';
import useDateState from '~/modules/common/charts/timeline/calculations/useDateState';
import { useMeContext } from '~/modules/me';
import {
  getUserPreferenceWeekStart,
  getUserPreferenceWeekEnd
} from '~/modules/common/dates/dayOfWeek';
import { DEFAULT_OVERVIEW_UNIT } from '~/modules/resource-management/common/enums';
import {
  PERIOD_SCALE_ENUM,
  DEFAULT_PERIOD_SCALE
} from '~/modules/resourcing/common/enums';
import {
  usePutInsightsSettings,
  INSIGHTS_SETTINGS_FIELDS
} from './useInsightsSettings';

export const LUXON_SCALE = {
  [PERIOD_SCALE_ENUM.DAYS]: 'day',
  [PERIOD_SCALE_ENUM.WEEKS]: 'week',
  [PERIOD_SCALE_ENUM.MONTHS]: 'month',
  [PERIOD_SCALE_ENUM.QUARTERS]: 'quarter',
  [PERIOD_SCALE_ENUM.YEARS]: 'year'
};

export const useResourceOverviewToolbarContext = () =>
  useContext(ResourceOverviewToolbarContext);

export const useResourceManagementDashboardContextValue = ({
  insightsSettings
} = {}) => {
  const me = useMeContext();
  const { putInsightsSettings } = usePutInsightsSettings();

  const [periodScale, setPeriodScaleState] = useState(
    insightsSettings?.periodScale || DEFAULT_PERIOD_SCALE
  );
  const [displayUnit, setDisplayUnitState] = useState(
    insightsSettings?.displayUnit || DEFAULT_OVERVIEW_UNIT
  );

  const settingsRef = useRef({
    [INSIGHTS_SETTINGS_FIELDS.DISPLAY_UNIT]: insightsSettings?.displayUnit,
    [INSIGHTS_SETTINGS_FIELDS.PERIOD_SCALE]: insightsSettings?.periodScale,
    [INSIGHTS_SETTINGS_FIELDS.SELECTED_VIEW]: insightsSettings?.selectedView,
    [INSIGHTS_SETTINGS_FIELDS.GROUP_BY]: insightsSettings?.groupBy,
    [INSIGHTS_SETTINGS_FIELDS.SELECTED_PROBLEM]:
      insightsSettings?.selectedProblem
  });

  const updateInsightsSettings = useCallback(
    (key, value) => {
      if (!putInsightsSettings) return;
      settingsRef.current[key] = value;
      putInsightsSettings(settingsRef.current);
    },
    [putInsightsSettings]
  );

  const setPeriodScale = useCallback(
    newScale => {
      setPeriodScaleState(newScale);
      updateInsightsSettings(INSIGHTS_SETTINGS_FIELDS.PERIOD_SCALE, newScale);
    },
    [updateInsightsSettings]
  );

  const setDisplayUnit = useCallback(
    newUnit => {
      setDisplayUnitState(newUnit);
      updateInsightsSettings(INSIGHTS_SETTINGS_FIELDS.DISPLAY_UNIT, newUnit);
    },
    [updateInsightsSettings]
  );

  const [startDate, setStartDate] = useDateState({
    initialValue: DateTime.utc(),
    sessionStorageKey: 'resource-management-dashboard-start-date'
  });

  const dateRange = useMemo(() => {
    if (periodScale === PERIOD_SCALE_ENUM.WEEKS) {
      const weekStartDate = getUserPreferenceWeekStart(me, startDate);
      const weekEndDate = getUserPreferenceWeekEnd(me, startDate);

      return {
        startDate: weekStartDate,
        endDate: weekEndDate
      };
    }

    const luxonScale = LUXON_SCALE[periodScale];

    return {
      startDate: startDate.startOf(luxonScale),
      endDate: startDate.endOf(luxonScale)
    };
  }, [startDate, periodScale, me]);

  const onPreviousClick = useCallback(() => {
    const offsetToApply = scaleOffsets[periodScale];

    setStartDate(startDate.minus(offsetToApply));
  }, [periodScale, setStartDate, startDate]);

  const onNextClick = useCallback(() => {
    const offsetToApply = scaleOffsets[periodScale];

    setStartDate(startDate.plus(offsetToApply));
  }, [periodScale, setStartDate, startDate]);

  const setDate = useCallback(
    newDate => {
      setStartDate(newDate);
    },
    [setStartDate]
  );

  return useMemo(
    () => ({
      periodScale,
      setPeriodScale,
      displayUnit,
      setDisplayUnit,
      insightsSettings,
      updateInsightsSettings,
      dateRange,
      onPreviousClick,
      onNextClick,
      setDate
    }),
    [
      periodScale,
      setPeriodScale,
      displayUnit,
      setDisplayUnit,
      insightsSettings,
      updateInsightsSettings,
      dateRange,
      onPreviousClick,
      onNextClick,
      setDate
    ]
  );
};

export default useResourceOverviewToolbarContext;
